// Building Risk Assessment Scenarios (Section W).
import { useState, useEffect } from "react";
import { Plus, Trash2, Zap, ChevronDown, ChevronRight, Sparkles, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Pill from "@/components/Pill";
import { inputCls, RATING_TONE } from "./shared";
import EvidencePanel from "./EvidencePanel";

export default function Scenarios({ rid, scenarios = [], matrix, canEdit, onChange }) {
    const [open, setOpen] = useState(null);
    const [adding, setAdding] = useState(false);
    const [bulkDrafts, setBulkDrafts] = useState({});
    const [bulkBusy, setBulkBusy] = useState(false);
    const sev = matrix?.severities || [];
    const like = matrix?.likelihoods || [];

    async function add(cat) {
        try { await api.post(`/bsc/${rid}/scenario`, { category: cat, title: "New scenario" }); toast.success("Scenario added"); setAdding(false); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function patch(sc, body) {
        try { await api.patch(`/bsc/${rid}/scenario/${sc.id}`, body); onChange?.(); }
        catch (e) { toast.error("Failed"); }
    }
    async function createAction(sc) {
        try { await api.post(`/bsc/${rid}/scenario/${sc.id}/create-action`, { priority: "High" }); toast.success("Action created & linked"); onChange?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }
    async function remove(sc) {
        if (!window.confirm("Delete this scenario?")) return;
        try { await api.delete(`/bsc/${rid}/scenario/${sc.id}`); onChange?.(); } catch { toast.error("Failed"); }
    }
    async function generateAll() {
        setBulkBusy(true);
        try {
            const { data } = await api.post(`/bsc/${rid}/scenarios/ai-conclusions?only_missing=true`);
            const drafts = data.drafts || {};
            setBulkDrafts(drafts);
            const n = Object.keys(drafts).length;
            const errN = Object.keys(data.errors || {}).length;
            if (n) toast.success(`Drafted ${n} conclusion${n === 1 ? "" : "s"} — review and apply each${errN ? ` (${errN} failed)` : ""}`);
            else toast.info(data.considered === 0 ? "All scenarios already have a conclusion" : "No conclusions were generated");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk generation failed"); }
        finally { setBulkBusy(false); }
    }
    const clearBulk = (id) => setBulkDrafts((d) => { const n = { ...d }; delete n[id]; return n; });

    const byCat = { Fire: scenarios.filter((s) => s.category === "Fire"), Structural: scenarios.filter((s) => s.category === "Structural") };

    return (
        <div data-testid="bscr-scenarios" className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-[18px] font-bold text-[#1F1F1F]">Building Risk Assessment Scenarios</h2>
                    <p className="text-[12.5px] text-[#6F6A64]">Assess each fire and structural scenario — ratings are derived from the risk matrix.</p>
                </div>
                {canEdit && scenarios.length > 0 && (
                    <button data-testid="bscr-scenarios-generate-all" onClick={generateAll} disabled={bulkBusy}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-semibold text-white disabled:opacity-60"
                        style={{ background: "linear-gradient(90deg,#2A5DB0,#1F8A8A)" }}>
                        {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Generate all conclusions (AI)
                    </button>
                )}
            </div>

            {["Fire", "Structural"].map((cat) => (
                <div key={cat}>
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#6F6A64]">{cat} scenarios ({byCat[cat].length})</h3>
                        {canEdit && <button data-testid={`bscr-scenario-add-${cat}`} onClick={() => add(cat)} className="inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}><Plus size={13} /> Add {cat.toLowerCase()} scenario</button>}
                    </div>
                    <div className="space-y-2">
                        {byCat[cat].map((sc) => (
                            <div key={sc.id} data-testid={`bscr-scenario-${sc.id}`} className="rounded-xl border border-[#EFEAE4] bg-white">
                                <button onClick={() => setOpen(open === sc.id ? null : sc.id)} className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {open === sc.id ? <ChevronDown size={15} className="text-[#9A968F]" /> : <ChevronRight size={15} className="text-[#9A968F]" />}
                                        <span className="text-[13px] font-semibold text-[#1F1F1F] truncate">{sc.title}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {bulkDrafts[sc.id] && open !== sc.id && <Pill tone="blue">AI draft ready</Pill>}
                                        {sc.linked_action_id && <Pill tone="blue">Action</Pill>}
                                        {sc.risk_rating && <Pill tone={RATING_TONE[sc.risk_rating] || "neutral"}>{sc.risk_rating}</Pill>}
                                    </div>
                                </button>
                                {open === sc.id && (
                                    <div className="px-3.5 pb-3.5 space-y-2.5 border-t border-[#F4F0EB] pt-3">
                                        <input data-testid={`bscr-scenario-title-${sc.id}`} disabled={!canEdit} defaultValue={sc.title} onBlur={(e) => e.target.value !== sc.title && patch(sc, { title: e.target.value })} className={inputCls} />
                                        <div className="grid sm:grid-cols-2 gap-2.5">
                                            <Labeled label="Existing controls"><textarea disabled={!canEdit} rows={2} defaultValue={sc.existing_controls || ""} onBlur={(e) => patch(sc, { existing_controls: e.target.value })} className={inputCls} /></Labeled>
                                            <Labeled label="Missing controls"><textarea disabled={!canEdit} rows={2} defaultValue={sc.missing_controls || ""} onBlur={(e) => patch(sc, { missing_controls: e.target.value })} className={inputCls} /></Labeled>
                                        </div>
                                        <Labeled label="Further action required"><textarea disabled={!canEdit} rows={2} defaultValue={sc.further_action || ""} onBlur={(e) => patch(sc, { further_action: e.target.value })} className={inputCls} /></Labeled>
                                        <ScenarioConclusion rid={rid} sc={sc} canEdit={canEdit} patch={patch}
                                            initialDraft={bulkDrafts[sc.id]} onClearDraft={() => clearBulk(sc.id)} />
                                        <div className="grid sm:grid-cols-3 gap-2.5">
                                            <Labeled label="Severity">
                                                <select data-testid={`bscr-scenario-severity-${sc.id}`} disabled={!canEdit} value={sc.severity || ""} onChange={(e) => patch(sc, { severity: e.target.value })} className={inputCls}>
                                                    <option value="">—</option>{sev.map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </Labeled>
                                            <Labeled label="Likelihood">
                                                <select data-testid={`bscr-scenario-likelihood-${sc.id}`} disabled={!canEdit} value={sc.likelihood || ""} onChange={(e) => patch(sc, { likelihood: e.target.value })} className={inputCls}>
                                                    <option value="">—</option>{like.map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </Labeled>
                                            <Labeled label="Risk rating (auto)"><div className="px-3 py-2"><Pill tone={RATING_TONE[sc.risk_rating] || "neutral"}>{sc.risk_rating || "Not rated"}</Pill></div></Labeled>
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-3 pt-1">
                                            <EvidencePanel rid={rid} scenarioId={sc.id} code={null} evidence={sc.evidence || []} canEdit={canEdit} onChange={onChange} compact />
                                            {canEdit && (
                                                <div className="flex items-end justify-end gap-3">
                                                    {!sc.linked_action_id && <button data-testid={`bscr-scenario-action-${sc.id}`} onClick={() => createAction(sc)} className="text-[12px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--brand-accent,#C75A1B)" }}><Zap size={12} /> Create action</button>}
                                                    <button onClick={() => remove(sc)} className="text-[12px] text-[#9A968F] hover:text-[#B33A2E] inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {byCat[cat].length === 0 && <p className="text-[12px] italic text-[#9A968F]">No {cat.toLowerCase()} scenarios yet.</p>}
                    </div>
                </div>
            ))}
        </div>
    );
}

function Labeled({ label, children }) {
    return <div><label className="block text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F] mb-1">{label}</label>{children}</div>;
}

function ScenarioConclusion({ rid, sc, canEdit, patch, initialDraft, onClearDraft }) {
    const [draft, setDraft] = useState(initialDraft ?? null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (initialDraft != null) setDraft(initialDraft); }, [initialDraft]);

    async function generate() {
        setBusy(true);
        try { const { data } = await api.post(`/bsc/${rid}/scenario/${sc.id}/ai-conclusion`); setDraft(data.draft || ""); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI generation failed"); }
        finally { setBusy(false); }
    }
    async function apply() {
        await patch(sc, { conclusion: draft });
        setDraft(null);
        onClearDraft?.();
        toast.success("Conclusion applied — review and save");
    }
    function discard() { setDraft(null); onClearDraft?.(); }

    return (
        <div data-testid={`bscr-scenario-conclusion-${sc.id}`} className="rounded-lg border border-[#EFEAE4] bg-[#FCFBF9] p-2.5 space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-[10.5px] font-bold uppercase tracking-wider text-[#9A968F]">Conclusion</label>
                {canEdit && (
                    <button data-testid={`bscr-scenario-ai-conclusion-${sc.id}`} onClick={generate} disabled={busy}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-1 rounded-md text-white disabled:opacity-50"
                        style={{ background: "linear-gradient(90deg,#2A5DB0,#1F8A8A)" }}>
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Generate conclusion (AI)
                    </button>
                )}
            </div>
            <textarea data-testid={`bscr-scenario-conclusion-text-${sc.id}`} disabled={!canEdit} rows={2}
                defaultValue={sc.conclusion || ""} key={sc.conclusion || ""}
                onBlur={(e) => e.target.value !== (sc.conclusion || "") && patch(sc, { conclusion: e.target.value })}
                placeholder="Summarise the residual risk position, or generate a draft with AI."
                className={inputCls} />
            {draft !== null && (
                <div data-testid={`bscr-scenario-conclusion-draft-${sc.id}`} className="rounded-lg border-2 border-[#CBD9F0] bg-white p-2.5 space-y-2">
                    <div className="flex items-center gap-2">
                        <Sparkles size={12} className="text-[#2A5DB0]" />
                        <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#2A5DB0]">AI draft · requires review</span>
                    </div>
                    <p className="text-[12.5px] text-[#3e3e3e] whitespace-pre-wrap">{draft}</p>
                    {canEdit && (
                        <div className="flex items-center gap-3">
                            <button data-testid={`bscr-scenario-conclusion-apply-${sc.id}`} onClick={apply}
                                className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>Apply to conclusion</button>
                            <button onClick={generate} disabled={busy} className="text-[11.5px] font-semibold text-[#2A5DB0] disabled:opacity-50">Regenerate</button>
                            <button onClick={discard} className="text-[11.5px] text-[#9A968F]">Discard</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
